import {
  Loader2,
  Maximize,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  Volume1,
  Volume2,
  VolumeX,
} from 'lucide-react';
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type Ref,
  type SyntheticEvent,
} from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './VideoPlayer.module.scss';

const SPEEDS = [1, 1.25, 1.5, 2, 0.5];
/** Через сколько спрятать панель, пока видео идёт, а мышь стоит. */
const IDLE_MS = 2500;

export interface VideoPlayerProps {
  src: string;
  /** Видео или аудио: у аудио — только панель. */
  kind?: 'video' | 'audio';
  poster?: string;
  /**
   * Кадр на секунде at — картинка над полосой перемотки при наведении,
   * как на YouTube. Без него — только время.
   */
  preview?: (at: number) => string;
  /** На сколько секунд мотают стрелки. В окне выбора кадра — на кадр. */
  seekStep?: number;
  preload?: 'none' | 'metadata' | 'auto';
  autoFocus?: boolean;
  className?: string | undefined;
  mediaRef?: Ref<HTMLMediaElement>;
  onTimeUpdate?: (time: number) => void;
  onDuration?: (duration: number) => void;
  onError?: () => void;
}

/**
 * Проигрыватель видео и аудио в стиле Faceta вместо стандартного
 * браузерного: у него в каждом браузере свой вид и свой набор кнопок.
 *
 * Всё управление — в одном блоке, и в полный экран уходит он целиком, а не
 * голый `<video>`: иначе браузер подменил бы панель своей. Поэтому же
 * никаких всплывающих окон в портале — в полноэкранном режиме их не видно;
 * скорость переключается по кругу одной кнопкой.
 *
 * Клавиши (когда плеер в фокусе): пробел и K — пуск и пауза, стрелки —
 * перемотка на seekStep, M — звук, F — полный экран.
 */
export function VideoPlayer({
  src,
  kind = 'video',
  poster,
  preview,
  seekStep = 5,
  preload = 'metadata',
  autoFocus = false,
  className,
  mediaRef,
  onTimeUpdate,
  onDuration,
  onError,
}: VideoPlayerProps) {
  const root = useRef<HTMLDivElement>(null);
  const media = useRef<HTMLMediaElement | null>(null);
  const idle = useRef<number>(undefined);

  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [ended, setEnded] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState<[number, number][]>([]);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [active, setActive] = useState(true);

  const audio = kind === 'audio';

  // Снаружи (окно выбора кадра) нужен сам элемент — перематывать.
  useImperativeHandle(mediaRef, () => media.current!, []);

  const attach = (node: HTMLMediaElement | null) => {
    media.current = node;
  };

  useEffect(() => {
    const sync = () => {
      setFullscreen(document.fullscreenElement === root.current);
    };

    document.addEventListener('fullscreenchange', sync);

    return () => {
      document.removeEventListener('fullscreenchange', sync);
    };
  }, []);

  useEffect(() => {
    if (autoFocus) {
      root.current?.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  useEffect(() => () => window.clearTimeout(idle.current), []);

  // Панель видна, пока мышь двигается или видео на паузе.
  const wake = () => {
    setActive(true);
    window.clearTimeout(idle.current);
    idle.current = window.setTimeout(() => {
      setActive(false);
    }, IDLE_MS);
  };

  const toggle = () => {
    const el = media.current;

    if (el === null) return;

    if (el.paused || el.ended) {
      void el.play().catch(() => {
        // Браузер не дал запустить (нет файла, запрет автозапуска) —
        // состояние останется «на паузе», ошибку покажет onError.
      });
    } else {
      el.pause();
    }
  };

  const seekTo = (at: number) => {
    const el = media.current;

    if (el === null || !Number.isFinite(el.duration)) return;

    el.currentTime = Math.min(Math.max(0, at), el.duration);
    setTime(el.currentTime);
  };

  const changeVolume = (next: number) => {
    const el = media.current;

    if (el === null) return;

    el.volume = Math.min(Math.max(0, next), 1);
    el.muted = el.volume === 0;
  };

  const toggleMute = () => {
    const el = media.current;

    if (el === null) return;

    el.muted = !el.muted;

    if (!el.muted && el.volume === 0) {
      el.volume = 0.5;
    }
  };

  const cycleSpeed = () => {
    const el = media.current;

    if (el === null) return;

    el.playbackRate = SPEEDS[(SPEEDS.indexOf(el.playbackRate) + 1) % SPEEDS.length] ?? 1;
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement !== null) {
      void document.exitFullscreen();
    } else {
      void root.current?.requestFullscreen();
    }
  };

  const pip = () => {
    const el = media.current;

    if (!(el instanceof HTMLVideoElement)) return;

    if (document.pictureInPictureElement === el) {
      void document.exitPictureInPicture();
    } else {
      void el.requestPictureInPicture().catch(() => {
        // Не поддерживается или запрещено — кнопка просто ничего не делает.
      });
    }
  };

  const keys = (event: KeyboardEvent<HTMLDivElement>) => {
    // Клавиши внутри полосы перемотки и громкости обрабатывают они сами.
    if ((event.target as HTMLElement).closest('[role="slider"]') !== null) return;

    const handled = (() => {
      switch (event.key) {
        case ' ':
        case 'k':
        case 'л':
          toggle();
          return true;
        case 'ArrowLeft':
          seekTo(time - seekStep);
          return true;
        case 'ArrowRight':
          seekTo(time + seekStep);
          return true;
        case 'm':
        case 'ь':
          toggleMute();
          return true;
        case 'f':
        case 'а':
          if (!audio) toggleFullscreen();
          return !audio;
        default:
          return false;
      }
    })();

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
      wake();
    }
  };

  const events = {
    onPlay: () => {
      setPlaying(true);
      setStarted(true);
      setEnded(false);
      wake();
    },
    onPause: () => {
      setPlaying(false);
      setActive(true);
    },
    onEnded: () => {
      setPlaying(false);
      setEnded(true);
      setActive(true);
    },
    onWaiting: () => {
      setWaiting(true);
    },
    onPlaying: () => {
      setWaiting(false);
    },
    onCanPlay: () => {
      setWaiting(false);
    },
    onTimeUpdate: (event: SyntheticEvent<HTMLMediaElement>) => {
      setTime(event.currentTarget.currentTime);
      onTimeUpdate?.(event.currentTarget.currentTime);
    },
    onSeeked: (event: SyntheticEvent<HTMLMediaElement>) => {
      setTime(event.currentTarget.currentTime);
      onTimeUpdate?.(event.currentTarget.currentTime);
    },
    onLoadedMetadata: (event: SyntheticEvent<HTMLMediaElement>) => {
      const value = event.currentTarget.duration;

      if (Number.isFinite(value)) {
        setDuration(value);
        onDuration?.(value);
      }
    },
    onDurationChange: (event: SyntheticEvent<HTMLMediaElement>) => {
      const value = event.currentTarget.duration;

      if (Number.isFinite(value)) {
        setDuration(value);
      }
    },
    onProgress: (event: SyntheticEvent<HTMLMediaElement>) => {
      const ranges = event.currentTarget.buffered;

      setBuffered(
        Array.from({ length: ranges.length }, (_, i) => [ranges.start(i), ranges.end(i)]),
      );
    },
    onVolumeChange: (event: SyntheticEvent<HTMLMediaElement>) => {
      setVolume(event.currentTarget.volume);
      setMuted(event.currentTarget.muted);
    },
    onRateChange: (event: SyntheticEvent<HTMLMediaElement>) => {
      setSpeed(event.currentTarget.playbackRate);
    },
    onError: () => {
      onError?.();
    },
  };

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const showControls = audio || active || !playing;

  return (
    <div
      ref={root}
      className={cx(
        styles['player'],
        audio && styles['player--audio'],
        fullscreen && styles['player--fullscreen'],
        !showControls && styles['player--idle'],
        className,
      )}
      tabIndex={0}
      aria-label={audio ? 'Проигрыватель аудио' : 'Проигрыватель видео'}
      onKeyDown={keys}
      onPointerMove={wake}
      onPointerLeave={() => {
        if (playing) setActive(false);
      }}
    >
      {audio ? (
        <audio ref={attach} src={src} preload={preload} {...events} />
      ) : (
        <video
          ref={attach}
          className={styles['video']}
          src={src}
          poster={poster}
          preload={preload}
          playsInline
          onClick={toggle}
          onDoubleClick={toggleFullscreen}
          {...events}
        />
      )}

      {!audio && waiting && playing && (
        <span className={styles['spinner']} aria-hidden="true">
          <Loader2 size={34} />
        </span>
      )}

      {!audio && !playing && !waiting && (
        <button
          type="button"
          className={cx(styles['big'], started && !ended && styles['big--quiet'])}
          aria-label={ended ? 'Смотреть снова' : 'Смотреть'}
          onClick={toggle}
        >
          {ended ? <RotateCcw size={26} /> : <Play size={28} />}
        </button>
      )}

      <div className={styles['bar']}>
        <Timeline
          time={time}
          duration={duration}
          buffered={buffered}
          preview={audio ? undefined : preview}
          step={seekStep}
          onSeek={seekTo}
          onScrub={wake}
        />

        <div className={styles['row']}>
          <button
            type="button"
            className={styles['button']}
            aria-label={playing ? 'Пауза' : 'Смотреть'}
            title={playing ? 'Пауза (K)' : 'Смотреть (K)'}
            onClick={toggle}
          >
            {playing ? <Pause size={17} /> : <Play size={17} />}
          </button>

          <div className={styles['volume']}>
            <button
              type="button"
              className={styles['button']}
              aria-label={muted ? 'Включить звук' : 'Выключить звук'}
              title={muted ? 'Включить звук (M)' : 'Выключить звук (M)'}
              onClick={toggleMute}
            >
              <VolumeIcon size={17} />
            </button>
            <input
              type="range"
              className={styles['volume__range']}
              role="slider"
              aria-label="Громкость"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              style={{ '--fill': `${String((muted ? 0 : volume) * 100)}%` } as object}
              onChange={(event) => {
                changeVolume(Number(event.target.value));
              }}
            />
          </div>

          <span className={styles['time']}>
            {format(time, duration)}{' '}
            <span className={styles['time__total']}>/ {format(duration, duration)}</span>
          </span>

          <span className={styles['spacer']} />

          <button
            type="button"
            className={cx(styles['button'], styles['speed'])}
            aria-label={`Скорость ${String(speed)}×, сменить`}
            title="Скорость"
            onClick={cycleSpeed}
          >
            {speed}×
          </button>

          {!audio && document.pictureInPictureEnabled && (
            <button
              type="button"
              className={styles['button']}
              aria-label="Картинка в картинке"
              title="Картинка в картинке"
              onClick={pip}
            >
              <PictureInPicture2 size={17} />
            </button>
          )}

          {!audio && (
            <button
              type="button"
              className={styles['button']}
              aria-label={fullscreen ? 'Выйти из полного экрана' : 'Во весь экран'}
              title={fullscreen ? 'Выйти из полного экрана (F)' : 'Во весь экран (F)'}
              onClick={toggleFullscreen}
            >
              {fullscreen ? <Minimize size={17} /> : <Maximize size={17} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Полоса перемотки: загруженное, просмотренное, время под курсором и кадр
 * над ним. Тянется мышью и пальцем; с клавиатуры — стрелки, Home, End.
 */
function Timeline({
  time,
  duration,
  buffered,
  preview,
  step,
  onSeek,
  onScrub,
}: {
  time: number;
  duration: number;
  buffered: [number, number][];
  preview: ((at: number) => string) | undefined;
  step: number;
  onSeek: (at: number) => void;
  onScrub: () => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ at: number; x: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const at = (event: PointerEvent<HTMLDivElement>) => {
    const box = track.current?.getBoundingClientRect();

    if (box === undefined || duration === 0) return null;

    const x = Math.min(Math.max(0, event.clientX - box.left), box.width);

    return { at: (x / box.width) * duration, x, width: box.width };
  };

  const percent = (value: number) => (duration > 0 ? `${String((value / duration) * 100)}%` : '0%');

  // Кадр под курсором — не на каждый пиксель, а с шагом: иначе при
  // движении мыши сервер получил бы сотню запросов.
  const frameAt =
    hover === null || duration === 0
      ? null
      : Math.min(
          duration,
          Math.round(hover.at / Math.max(1, duration / 40)) * Math.max(1, duration / 40),
        );

  return (
    <div
      ref={track}
      className={cx(styles['timeline'], dragging && styles['timeline--dragging'])}
      role="slider"
      tabIndex={0}
      aria-label="Перемотка"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(time)}
      aria-valuetext={`${format(time, duration)} из ${format(duration, duration)}`}
      onPointerDown={(event) => {
        const point = at(event);

        if (point === null) return;

        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
        onSeek(point.at);
      }}
      onPointerMove={(event) => {
        const point = at(event);

        if (point === null) return;

        setHover({ at: point.at, x: point.x });
        onScrub();

        if (dragging) {
          onSeek(point.at);
        }
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
      }}
      onPointerLeave={() => {
        if (!dragging) setHover(null);
      }}
      onKeyDown={(event) => {
        const next =
          event.key === 'ArrowLeft' || event.key === 'ArrowDown'
            ? time - step
            : event.key === 'ArrowRight' || event.key === 'ArrowUp'
              ? time + step
              : event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? duration
                  : null;

        if (next !== null) {
          event.preventDefault();
          event.stopPropagation();
          onSeek(next);
        }
      }}
    >
      <div className={styles['timeline__track']}>
        {buffered.map(([start, end]) => (
          <span
            key={start}
            className={styles['timeline__buffered']}
            style={{ left: percent(start), width: percent(end - start) }}
          />
        ))}
        <span className={styles['timeline__played']} style={{ width: percent(time) }} />
        <span className={styles['timeline__thumb']} style={{ left: percent(time) }} />
      </div>

      {hover !== null && duration > 0 && (
        <span className={styles['hover']} style={{ left: `${String(hover.x)}px` }}>
          {preview !== undefined && frameAt !== null && (
            <img className={styles['hover__frame']} src={preview(frameAt)} alt="" />
          )}
          <span className={styles['hover__time']}>{format(hover.at, duration)}</span>
        </span>
      )}
    </div>
  );
}

/** 0:07, 1:05:09 — часы только у длинного. */
function format(seconds: number, total: number): string {
  const value = Math.max(0, Math.floor(seconds));
  const h = Math.floor(value / 3600);
  const m = Math.floor((value % 3600) / 60);
  const s = String(value % 60).padStart(2, '0');

  return total >= 3600 ? `${String(h)}:${String(m).padStart(2, '0')}:${s}` : `${String(m)}:${s}`;
}
